import { LeadForm } from '../components/LeadForm';
import { useLanguage } from '../context/LanguageContext';
import { 
  Phone,
  Mail,
  MapPin,
  Clock,
  CheckCircle
} from 'lucide-react';

export default function ContactPage() {
  const { t } = useLanguage();

  return (
    <div className="pt-20" data-testid="contact-page">
      {/* Hero Section */}
      <section className="bg-slate-900 py-20 md:py-28">
        <div className="container-main">
          <div className="max-w-3xl">
            <p className="font-body text-amber-500 uppercase tracking-widest text-sm font-semibold mb-4">
              {t('contact.pageSubtitle')}
            </p>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold text-white mb-6">
              {t('contact.pageTitle')}
            </h1>
            <p className="font-body text-slate-300 text-lg">
              {t('contact.pageDesc')}
            </p>
          </div>
        </div>
      </section>

      {/* Form Section */}
      <section className="section-padding bg-white">
        <div className="container-main">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
            {/* Form */}
            <div className="lg:col-span-2">
              <div className="bg-slate-50 border border-slate-200 p-8 md:p-10">
                <h2 className="font-heading text-2xl font-bold text-slate-900 mb-2">
                  {t('contact.formTitle')}
                </h2>
                <p className="font-body text-slate-600 mb-8">
                  {t('contact.formDesc')}
                </p>
                <LeadForm variant="full" />
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-8">
              {/* Contact Info */}
              <div className="bg-slate-900 p-8">
                <h3 className="font-heading text-xl font-bold text-white mb-6">
                  {t('contact.contactInfo')}
                </h3>
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <Phone className="w-5 h-5 text-amber-500 mt-1" />
                    <div>
                      <p className="font-body text-slate-400 text-sm">{t('contact.phone')}</p>
                      <a href="tel:424-303-2130" className="font-body text-white text-lg hover:text-amber-500 transition-colors duration-200">
                        424-303-2130
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Mail className="w-5 h-5 text-amber-500 mt-1" />
                    <div>
                      <p className="font-body text-slate-400 text-sm">{t('contact.email')}</p>
                      <a href="mailto:ganninvesting@gmail.com" className="font-body text-white hover:text-amber-500 transition-colors duration-200">
                        ganninvesting@gmail.com
                      </a>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <MapPin className="w-5 h-5 text-amber-500 mt-1" />
                    <div>
                      <p className="font-body text-slate-400 text-sm">{t('contact.office')}</p>
                      <p className="font-body text-white">
                        1901 Avenue of the Stars #200<br />
                        Los Angeles, CA 90067
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <Clock className="w-5 h-5 text-amber-500 mt-1" />
                    <div>
                      <p className="font-body text-slate-400 text-sm">{t('contact.businessHours')}</p>
                      <p className="font-body text-white">
                        {t('contact.businessHoursValue')}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* What to Expect */}
              <div className="border border-slate-200 p-8">
                <h3 className="font-heading text-xl font-bold text-slate-900 mb-6">
                  {t('contact.whatToExpect')}
                </h3>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-amber-700 mt-0.5 flex-shrink-0" />
                    <span className="font-body text-slate-600 text-sm">
                      {t('contact.expect1')}
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-amber-700 mt-0.5 flex-shrink-0" />
                    <span className="font-body text-slate-600 text-sm">
                      {t('contact.expect2')}
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-amber-700 mt-0.5 flex-shrink-0" />
                    <span className="font-body text-slate-600 text-sm">
                      {t('contact.expect3')}
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-amber-700 mt-0.5 flex-shrink-0" />
                    <span className="font-body text-slate-600 text-sm">
                      {t('contact.expect4')}
                    </span>
                  </li>
                </ul>
              </div>

              {/* Loan Highlights */}
              <div className="bg-slate-50 border border-slate-200 p-8">
                <h3 className="font-heading text-xl font-bold text-slate-900 mb-6">
                  {t('contact.loanHighlights')}
                </h3>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="font-body text-slate-600">{t('contact.closingTime')}</span>
                    <span className="font-heading font-bold text-slate-900">5-7 Days</span>
                  </div>
                  <div className="border-b border-slate-200" />
                  <div className="flex justify-between items-center">
                    <span className="font-body text-slate-600">{t('contact.maxLtv')}</span>
                    <span className="font-heading font-bold text-slate-900">Up to 75%</span>
                  </div>
                  <div className="border-b border-slate-200" />
                  <div className="flex justify-between items-center">
                    <span className="font-body text-slate-600">{t('contact.loanRange')}</span>
                    <span className="font-heading font-bold text-slate-900">$75K - $10M</span>
                  </div>
                  <div className="border-b border-slate-200" />
                  <div className="flex justify-between items-center">
                    <span className="font-body text-slate-600">{t('contact.propertyTypes')}</span>
                    <span className="font-heading font-bold text-slate-900">{t('contact.resComm')}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="bg-slate-50 py-12">
        <div className="container-main">
          <p className="font-body text-xs text-slate-500 text-center max-w-4xl mx-auto">
            {t('quote.consent')} {t('footer.disclaimer')}
          </p>
        </div>
      </section>
    </div>
  );
}
